export type PortCallCaseLike = {
  id: number;
  vesselId?: number | null;
  vessel?: { vesselName?: string | null } | null;
  portId?: number | null;
  port?: { name?: string | null; code?: string | null } | null;
  eta?: string | null;
};

export type PortCallRequisitionLike = {
  id: number;
  vesselId?: number | null;
  vessel?: { vesselName?: string | null } | null;
  caseId?: number | null;
};

export type PortCallGroupBy =
  | 'vessel-port-eta'
  | 'vessel-eta-port'
  | 'port-vessel-eta'
  | 'eta-vessel-port'
  | 'vessel-date-port'
  | 'vessel-year-month-eta-port';

export const PORT_CALL_GROUP_BY_OPTIONS: Array<{
  value: PortCallGroupBy;
  label: string;
  shortLabel: string;
}> = [
  { value: 'vessel-port-eta', label: 'Vessel → Port → ETA', shortLabel: 'Vessel / Port / ETA' },
  { value: 'vessel-eta-port', label: 'Vessel → ETA → Port', shortLabel: 'Vessel / ETA / Port' },
  { value: 'port-vessel-eta', label: 'Port → Vessel → ETA', shortLabel: 'Port / Vessel / ETA' },
  { value: 'eta-vessel-port', label: 'ETA → Vessel → Port', shortLabel: 'ETA / Vessel / Port' },
  { value: 'vessel-date-port', label: 'Vessel → Date  Port', shortLabel: 'Vessel / Date  Port' },
  {
    value: 'vessel-year-month-eta-port',
    label: 'Vessel → Year → Month → Date  Port',
    shortLabel: 'Vessel / Year / Month / Date  Port',
  },
];

export const DEFAULT_PORT_CALL_GROUP_BY: PortCallGroupBy = 'vessel-date-port';

const GROUP_BY_SET = new Set<string>(PORT_CALL_GROUP_BY_OPTIONS.map((o) => o.value));

/** Accept current ids; migrate the previous combined default id if needed. */
export function normalizePortCallGroupBy(value: unknown): PortCallGroupBy {
  if (typeof value === 'string' && GROUP_BY_SET.has(value)) return value as PortCallGroupBy;
  return DEFAULT_PORT_CALL_GROUP_BY;
}

export function isPortCallGroupBy(value: unknown): value is PortCallGroupBy {
  return typeof value === 'string' && GROUP_BY_SET.has(value);
}

export type PortCallSelection = {
  vesselKey?: string;
  /** Special folder under vessel: requisitions not yet linked to a supply case. */
  folder?: 'orphan-reqs';
  yearKey?: string;
  monthKey?: string;
  portKey?: string;
  dateKey?: string;
};

export type PortCallTreeIcon = 'ship' | 'folder' | 'port' | 'calendar' | 'year' | 'month';

export type PortCallTreeNode = {
  key: string;
  label: string;
  count: number;
  muted?: boolean;
  icon: PortCallTreeIcon;
  iconClassName?: string;
  selection: PortCallSelection;
  expandKey?: string;
  children?: PortCallTreeNode[];
};

const NO_VESSEL = '__no_vessel__';
const NO_PORT = '__no_port__';
const NO_DATE = '__no_date__';
const NO_YEAR = '__no_year__';
const NO_MONTH = '__no_month__';

export const ORPHAN_REQS_FOLDER = 'orphan-reqs' as const;

export const etaDateKey = (eta?: string | null): string => {
  if (!eta) return NO_DATE;
  const iso = eta.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : NO_DATE;
};

export const etaYearKey = (eta?: string | null): string => {
  const d = etaDateKey(eta);
  return d === NO_DATE ? NO_YEAR : d.slice(0, 4);
};

export const etaMonthKey = (eta?: string | null): string => {
  const d = etaDateKey(eta);
  return d === NO_DATE ? NO_MONTH : d.slice(0, 7);
};

export const vesselKeyOf = (c: Pick<PortCallCaseLike, 'vesselId'>): string =>
  c.vesselId != null ? `v:${c.vesselId}` : NO_VESSEL;

export const portKeyOf = (c: PortCallCaseLike): string =>
  c.portId != null ? `p:${c.portId}` : NO_PORT;

export const vesselLabelOf = (c: Pick<PortCallCaseLike, 'vesselId' | 'vessel'>): string =>
  c.vessel?.vesselName?.trim()
  || (c.vesselId != null ? `Vessel #${c.vesselId}` : 'No vessel');

export const portLabelOf = (c: PortCallCaseLike): string => {
  const name = c.port?.name?.trim();
  if (name) return name;
  if (c.portId != null) return `Port #${c.portId}`;
  return 'No port';
};

const reqVesselKey = (r: PortCallRequisitionLike): string =>
  r.vesselId != null ? `v:${r.vesselId}` : NO_VESSEL;

const reqVesselLabel = (r: PortCallRequisitionLike): string =>
  r.vessel?.vesselName?.trim()
  || (r.vesselId != null ? `Vessel #${r.vesselId}` : 'No vessel');

const sortLabel = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const yearLabel = (key: string) => (key === NO_YEAR ? 'No ETA' : key);

const monthLabel = (key: string) => {
  if (key === NO_MONTH) return 'No month';
  const m = Number(key.slice(5, 7));
  if (!Number.isFinite(m) || m < 1 || m > 12) return key;
  return MONTH_NAMES[m - 1] ?? key;
};

const etaLabel = (key: string, formatDate: (iso?: string | null) => string) =>
  (key === NO_DATE ? 'No ETA' : formatDate(key));

type CaseAcc = { label: string; cases: PortCallCaseLike[]; orphanReqIds: number[] };

function collectByVessel(
  cases: PortCallCaseLike[],
  orphanRequisitions: PortCallRequisitionLike[],
): Map<string, CaseAcc> {
  const vessels = new Map<string, CaseAcc>();
  const ensure = (key: string, label: string): CaseAcc => {
    let v = vessels.get(key);
    if (!v) {
      v = { label, cases: [], orphanReqIds: [] };
      vessels.set(key, v);
    }
    return v;
  };
  for (const c of cases) ensure(vesselKeyOf(c), vesselLabelOf(c)).cases.push(c);
  for (const r of orphanRequisitions) {
    if (r.caseId != null && r.caseId > 0) continue;
    ensure(reqVesselKey(r), reqVesselLabel(r)).orphanReqIds.push(r.id);
  }
  return vessels;
}

function orphanFolderNode(vesselKey: string, orphanReqIds: number[]): PortCallTreeNode {
  return {
    key: `${vesselKey}:orphan`,
    label: 'Pending Requisitions',
    count: orphanReqIds.length,
    icon: 'folder',
    iconClassName: 'text-violet-500 dark:text-violet-400',
    selection: { vesselKey, folder: ORPHAN_REQS_FOLDER },
  };
}

function sortVesselEntries(entries: Array<[string, CaseAcc]>): Array<[string, CaseAcc]> {
  return entries.sort(([aKey, a], [bKey, b]) => {
    if (aKey === NO_VESSEL) return 1;
    if (bKey === NO_VESSEL) return -1;
    return sortLabel(a.label, b.label);
  });
}

function sortByDateKeyDesc(aKey: string, bKey: string): number {
  if (aKey === NO_DATE && bKey !== NO_DATE) return 1;
  if (bKey === NO_DATE && aKey !== NO_DATE) return -1;
  return bKey.localeCompare(aKey);
}

function sortPorts(aKey: string, bKey: string, aLabel: string, bLabel: string): number {
  if (aKey === NO_PORT) return 1;
  if (bKey === NO_PORT) return -1;
  return sortLabel(aLabel, bLabel);
}

function pendingOrphansRoot(vessels: Map<string, CaseAcc>): PortCallTreeNode | null {
  const kids = sortVesselEntries(Array.from(vessels.entries()))
    .filter(([, v]) => v.orphanReqIds.length > 0)
    .map(([vKey, v]) => orphanFolderNode(vKey, v.orphanReqIds));
  if (kids.length === 0) return null;
  return {
    key: '__pending_root__',
    label: 'Pending Requisitions',
    count: kids.reduce((s, n) => s + n.count, 0),
    icon: 'folder',
    iconClassName: 'text-violet-500 dark:text-violet-400',
    selection: { folder: ORPHAN_REQS_FOLDER },
    expandKey: 'pending-root',
    children: kids,
  };
}

function vesselNode(
  vKey: string,
  label: string,
  children: PortCallTreeNode[],
  orphanCount: number,
  keyPrefix?: string,
  selectionExtra?: Pick<PortCallSelection, 'portKey' | 'dateKey'>,
): PortCallTreeNode {
  const caseCount = children
    .filter((c) => c.selection.folder !== ORPHAN_REQS_FOLDER)
    .reduce((s, n) => s + n.count, 0);
  const nodeKey = keyPrefix ? `${keyPrefix}:${vKey}` : vKey;
  return {
    key: nodeKey,
    label,
    count: caseCount + orphanCount,
    muted: vKey === NO_VESSEL,
    icon: 'ship',
    iconClassName: 'text-indigo-500 dark:text-indigo-400',
    selection: { vesselKey: vKey, ...selectionExtra },
    expandKey: keyPrefix ? `vessel:${keyPrefix}:${vKey}` : `vessel:${vKey}`,
    children,
  };
}

/** Vessel → Port → ETA */
function buildVesselPortEta(
  cases: PortCallCaseLike[],
  orphanRequisitions: PortCallRequisitionLike[],
  formatDate: (iso?: string | null) => string,
): PortCallTreeNode[] {
  const vessels = collectByVessel(cases, orphanRequisitions);
  return sortVesselEntries(Array.from(vessels.entries())).map(([vKey, v]) => {
    const ports = new Map<string, { label: string; dates: Map<string, PortCallCaseLike[]> }>();
    for (const c of v.cases) {
      const pKey = portKeyOf(c);
      let port = ports.get(pKey);
      if (!port) {
        port = { label: portLabelOf(c), dates: new Map() };
        ports.set(pKey, port);
      }
      const dKey = etaDateKey(c.eta);
      let list = port.dates.get(dKey);
      if (!list) {
        list = [];
        port.dates.set(dKey, list);
      }
      list.push(c);
    }

    const portNodes: PortCallTreeNode[] = Array.from(ports.entries())
      .map(([pKey, p]) => {
        const dateNodes: PortCallTreeNode[] = Array.from(p.dates.entries())
          .map(([dKey, list]) => ({
            key: `${vKey}:${pKey}:${dKey}`,
            label: etaLabel(dKey, formatDate),
            count: list.length,
            muted: dKey === NO_DATE,
            icon: 'calendar' as const,
            iconClassName: 'text-amber-600 dark:text-amber-400',
            selection: { vesselKey: vKey, portKey: pKey, dateKey: dKey },
          }))
          .sort((a, b) => sortByDateKeyDesc(a.selection.dateKey ?? '', b.selection.dateKey ?? ''));

        return {
          key: `${vKey}:${pKey}`,
          label: p.label,
          count: dateNodes.reduce((s, n) => s + n.count, 0),
          muted: pKey === NO_PORT,
          icon: 'port' as const,
          iconClassName: 'text-emerald-600 dark:text-emerald-400',
          selection: { vesselKey: vKey, portKey: pKey },
          expandKey: `port:${vKey}:${pKey}`,
          children: dateNodes,
        };
      })
      .sort((a, b) =>
        sortPorts(a.selection.portKey ?? '', b.selection.portKey ?? '', a.label, b.label),
      );

    return vesselNode(
      vKey,
      v.label,
      [orphanFolderNode(vKey, v.orphanReqIds), ...portNodes],
      v.orphanReqIds.length,
    );
  });
}

/** Vessel → ETA → Port */
function buildVesselEtaPort(
  cases: PortCallCaseLike[],
  orphanRequisitions: PortCallRequisitionLike[],
  formatDate: (iso?: string | null) => string,
): PortCallTreeNode[] {
  const vessels = collectByVessel(cases, orphanRequisitions);
  return sortVesselEntries(Array.from(vessels.entries())).map(([vKey, v]) => {
    const dates = new Map<string, { ports: Map<string, { label: string; cases: PortCallCaseLike[] }> }>();
    for (const c of v.cases) {
      const dKey = etaDateKey(c.eta);
      const pKey = portKeyOf(c);
      let date = dates.get(dKey);
      if (!date) {
        date = { ports: new Map() };
        dates.set(dKey, date);
      }
      let port = date.ports.get(pKey);
      if (!port) {
        port = { label: portLabelOf(c), cases: [] };
        date.ports.set(pKey, port);
      }
      port.cases.push(c);
    }

    const dateNodes: PortCallTreeNode[] = Array.from(dates.entries())
      .map(([dKey, date]) => {
        const portNodes: PortCallTreeNode[] = Array.from(date.ports.entries())
          .map(([pKey, port]) => ({
            key: `${vKey}:${dKey}:${pKey}`,
            label: port.label,
            count: port.cases.length,
            muted: pKey === NO_PORT,
            icon: 'port' as const,
            iconClassName: 'text-emerald-600 dark:text-emerald-400',
            selection: { vesselKey: vKey, dateKey: dKey, portKey: pKey },
          }))
          .sort((a, b) =>
            sortPorts(a.selection.portKey ?? '', b.selection.portKey ?? '', a.label, b.label),
          );

        return {
          key: `${vKey}:${dKey}`,
          label: etaLabel(dKey, formatDate),
          count: portNodes.reduce((s, n) => s + n.count, 0),
          muted: dKey === NO_DATE,
          icon: 'calendar' as const,
          iconClassName: 'text-amber-600 dark:text-amber-400',
          selection: { vesselKey: vKey, dateKey: dKey },
          expandKey: `eta:${vKey}:${dKey}`,
          children: portNodes,
        };
      })
      .sort((a, b) => sortByDateKeyDesc(a.selection.dateKey ?? '', b.selection.dateKey ?? ''));

    return vesselNode(
      vKey,
      v.label,
      [orphanFolderNode(vKey, v.orphanReqIds), ...dateNodes],
      v.orphanReqIds.length,
    );
  });
}

/** Port → Vessel → ETA */
function buildPortVesselEta(
  cases: PortCallCaseLike[],
  orphanRequisitions: PortCallRequisitionLike[],
  formatDate: (iso?: string | null) => string,
): PortCallTreeNode[] {
  const vessels = collectByVessel(cases, orphanRequisitions);
  const ports = new Map<string, {
    label: string;
    vessels: Map<string, { label: string; dates: Map<string, PortCallCaseLike[]>; orphanReqIds: number[] }>;
  }>();

  for (const c of cases) {
    const pKey = portKeyOf(c);
    const vKey = vesselKeyOf(c);
    const dKey = etaDateKey(c.eta);
    let port = ports.get(pKey);
    if (!port) {
      port = { label: portLabelOf(c), vessels: new Map() };
      ports.set(pKey, port);
    }
    let vessel = port.vessels.get(vKey);
    if (!vessel) {
      vessel = { label: vesselLabelOf(c), dates: new Map(), orphanReqIds: [] };
      port.vessels.set(vKey, vessel);
    }
    let list = vessel.dates.get(dKey);
    if (!list) {
      list = [];
      vessel.dates.set(dKey, list);
    }
    list.push(c);
  }

  // Attach orphans under vessel nodes when that vessel appears under a port.
  for (const [vKey, v] of Array.from(vessels.entries())) {
    if (v.orphanReqIds.length === 0) continue;
    for (const port of Array.from(ports.values())) {
      const vessel = port.vessels.get(vKey);
      if (vessel) vessel.orphanReqIds = v.orphanReqIds;
    }
  }

  const portNodes: PortCallTreeNode[] = Array.from(ports.entries())
    .map(([pKey, p]) => {
      const vesselNodes: PortCallTreeNode[] = Array.from(p.vessels.entries())
        .map(([vKey, vessel]) => {
          const dateNodes: PortCallTreeNode[] = Array.from(vessel.dates.entries())
            .map(([dKey, list]) => ({
              key: `${pKey}:${vKey}:${dKey}`,
              label: etaLabel(dKey, formatDate),
              count: list.length,
              muted: dKey === NO_DATE,
              icon: 'calendar' as const,
              iconClassName: 'text-amber-600 dark:text-amber-400',
              selection: { portKey: pKey, vesselKey: vKey, dateKey: dKey },
            }))
            .sort((a, b) => sortByDateKeyDesc(a.selection.dateKey ?? '', b.selection.dateKey ?? ''));

          const kids: PortCallTreeNode[] = [
            ...(vessel.orphanReqIds.length
              ? [orphanFolderNode(vKey, vessel.orphanReqIds)]
              : []),
            ...dateNodes,
          ];
          return vesselNode(vKey, vessel.label, kids, vessel.orphanReqIds.length, pKey, { portKey: pKey });
        })
        .sort((a, b) => {
          if (a.key === NO_VESSEL || a.key.endsWith(`:${NO_VESSEL}`)) return 1;
          if (b.key === NO_VESSEL || b.key.endsWith(`:${NO_VESSEL}`)) return -1;
          return sortLabel(a.label, b.label);
        });

      return {
        key: `port:${pKey}`,
        label: p.label,
        count: vesselNodes.reduce((s, n) => s + n.count, 0),
        muted: pKey === NO_PORT,
        icon: 'port' as const,
        iconClassName: 'text-emerald-600 dark:text-emerald-400',
        selection: { portKey: pKey },
        expandKey: `port:${pKey}`,
        children: vesselNodes,
      };
    })
    .sort((a, b) =>
      sortPorts(a.selection.portKey ?? '', b.selection.portKey ?? '', a.label, b.label),
    );

  const pending = pendingOrphansRoot(vessels);
  return pending ? [pending, ...portNodes] : portNodes;
}

/** ETA → Vessel → Port */
function buildEtaVesselPort(
  cases: PortCallCaseLike[],
  orphanRequisitions: PortCallRequisitionLike[],
  formatDate: (iso?: string | null) => string,
): PortCallTreeNode[] {
  const vessels = collectByVessel(cases, orphanRequisitions);
  const dates = new Map<string, {
    vessels: Map<string, { label: string; ports: Map<string, { label: string; cases: PortCallCaseLike[] }>; orphanReqIds: number[] }>;
  }>();

  for (const c of cases) {
    const dKey = etaDateKey(c.eta);
    const vKey = vesselKeyOf(c);
    const pKey = portKeyOf(c);
    let date = dates.get(dKey);
    if (!date) {
      date = { vessels: new Map() };
      dates.set(dKey, date);
    }
    let vessel = date.vessels.get(vKey);
    if (!vessel) {
      vessel = { label: vesselLabelOf(c), ports: new Map(), orphanReqIds: [] };
      date.vessels.set(vKey, vessel);
    }
    let port = vessel.ports.get(pKey);
    if (!port) {
      port = { label: portLabelOf(c), cases: [] };
      vessel.ports.set(pKey, port);
    }
    port.cases.push(c);
  }

  for (const [vKey, v] of Array.from(vessels.entries())) {
    if (v.orphanReqIds.length === 0) continue;
    for (const date of Array.from(dates.values())) {
      const vessel = date.vessels.get(vKey);
      if (vessel) vessel.orphanReqIds = v.orphanReqIds;
    }
  }

  const dateNodes: PortCallTreeNode[] = Array.from(dates.entries())
    .map(([dKey, date]) => {
      const vesselNodes: PortCallTreeNode[] = Array.from(date.vessels.entries())
        .map(([vKey, vessel]) => {
          const portNodes: PortCallTreeNode[] = Array.from(vessel.ports.entries())
            .map(([pKey, port]) => ({
              key: `${dKey}:${vKey}:${pKey}`,
              label: port.label,
              count: port.cases.length,
              muted: pKey === NO_PORT,
              icon: 'port' as const,
              iconClassName: 'text-emerald-600 dark:text-emerald-400',
              selection: { dateKey: dKey, vesselKey: vKey, portKey: pKey },
            }))
            .sort((a, b) =>
              sortPorts(a.selection.portKey ?? '', b.selection.portKey ?? '', a.label, b.label),
            );

          const kids: PortCallTreeNode[] = [
            ...(vessel.orphanReqIds.length
              ? [orphanFolderNode(vKey, vessel.orphanReqIds)]
              : []),
            ...portNodes,
          ];
          return vesselNode(vKey, vessel.label, kids, vessel.orphanReqIds.length, dKey, { dateKey: dKey });
        })
        .sort((a, b) => {
          if (a.key === NO_VESSEL || a.key.endsWith(`:${NO_VESSEL}`)) return 1;
          if (b.key === NO_VESSEL || b.key.endsWith(`:${NO_VESSEL}`)) return -1;
          return sortLabel(a.label, b.label);
        });

      return {
        key: `eta:${dKey}`,
        label: etaLabel(dKey, formatDate),
        count: vesselNodes.reduce((s, n) => s + n.count, 0),
        muted: dKey === NO_DATE,
        icon: 'calendar' as const,
        iconClassName: 'text-amber-600 dark:text-amber-400',
        selection: { dateKey: dKey },
        expandKey: `eta:${dKey}`,
        children: vesselNodes,
      };
    })
    .sort((a, b) => sortByDateKeyDesc(a.selection.dateKey ?? '', b.selection.dateKey ?? ''));

  const pending = pendingOrphansRoot(vessels);
  return pending ? [pending, ...dateNodes] : dateNodes;
}

/** Vessel → Date  Port (combined leaf) */
function buildVesselDatePort(
  cases: PortCallCaseLike[],
  orphanRequisitions: PortCallRequisitionLike[],
  formatDate: (iso?: string | null) => string,
): PortCallTreeNode[] {
  const vessels = collectByVessel(cases, orphanRequisitions);
  return sortVesselEntries(Array.from(vessels.entries())).map(([vKey, v]) => {
    const leaves = new Map<string, {
      label: string;
      muted: boolean;
      cases: PortCallCaseLike[];
      portKey: string;
      dateKey: string;
    }>();

    for (const c of v.cases) {
      const pKey = portKeyOf(c);
      const dKey = etaDateKey(c.eta);
      const leafKey = `${dKey}:${pKey}`;
      let leaf = leaves.get(leafKey);
      if (!leaf) {
        leaf = {
          label: `${etaLabel(dKey, formatDate)}  ${portLabelOf(c)}`,
          muted: pKey === NO_PORT || dKey === NO_DATE,
          cases: [],
          portKey: pKey,
          dateKey: dKey,
        };
        leaves.set(leafKey, leaf);
      }
      leaf.cases.push(c);
    }

    const leafNodes: PortCallTreeNode[] = Array.from(leaves.entries())
      .map(([leafKey, leaf]) => ({
        key: `${vKey}:${leafKey}`,
        label: leaf.label,
        count: leaf.cases.length,
        muted: leaf.muted,
        icon: 'port' as const,
        iconClassName: 'text-emerald-600 dark:text-emerald-400',
        selection: { vesselKey: vKey, portKey: leaf.portKey, dateKey: leaf.dateKey },
      }))
      .sort((a, b) => {
        const byDate = sortByDateKeyDesc(a.selection.dateKey ?? '', b.selection.dateKey ?? '');
        if (byDate !== 0) return byDate;
        return sortLabel(a.label, b.label);
      });

    return vesselNode(
      vKey,
      v.label,
      [orphanFolderNode(vKey, v.orphanReqIds), ...leafNodes],
      v.orphanReqIds.length,
    );
  });
}

/** Vessel → Year → Month → Date  Port */
function buildVesselYearMonthDatePort(
  cases: PortCallCaseLike[],
  orphanRequisitions: PortCallRequisitionLike[],
  formatDate: (iso?: string | null) => string,
): PortCallTreeNode[] {
  const vessels = collectByVessel(cases, orphanRequisitions);
  return sortVesselEntries(Array.from(vessels.entries())).map(([vKey, v]) => {
    type LeafBucket = {
      label: string;
      muted: boolean;
      cases: PortCallCaseLike[];
      dateKey: string;
      portKey: string;
    };
    type MonthBucket = { leaves: Map<string, LeafBucket> };
    type YearBucket = { months: Map<string, MonthBucket> };
    const years = new Map<string, YearBucket>();

    for (const c of v.cases) {
      const yKey = etaYearKey(c.eta);
      const mKey = etaMonthKey(c.eta);
      const dKey = etaDateKey(c.eta);
      const pKey = portKeyOf(c);
      const leafKey = `${dKey}:${pKey}`;
      let year = years.get(yKey);
      if (!year) {
        year = { months: new Map() };
        years.set(yKey, year);
      }
      let month = year.months.get(mKey);
      if (!month) {
        month = { leaves: new Map() };
        year.months.set(mKey, month);
      }
      let leaf = month.leaves.get(leafKey);
      if (!leaf) {
        leaf = {
          label: `${etaLabel(dKey, formatDate)}  ${portLabelOf(c)}`,
          muted: dKey === NO_DATE || pKey === NO_PORT,
          cases: [],
          dateKey: dKey,
          portKey: pKey,
        };
        month.leaves.set(leafKey, leaf);
      }
      leaf.cases.push(c);
    }

    const yearNodes: PortCallTreeNode[] = Array.from(years.entries())
      .map(([yKey, year]) => {
        const monthNodes: PortCallTreeNode[] = Array.from(year.months.entries())
          .map(([mKey, month]) => {
            const leafNodes: PortCallTreeNode[] = Array.from(month.leaves.entries())
              .map(([leafKey, leaf]) => ({
                key: `${vKey}:${yKey}:${mKey}:${leafKey}`,
                label: leaf.label,
                count: leaf.cases.length,
                muted: leaf.muted,
                icon: 'port' as const,
                iconClassName: 'text-emerald-600 dark:text-emerald-400',
                selection: {
                  vesselKey: vKey,
                  yearKey: yKey,
                  monthKey: mKey,
                  dateKey: leaf.dateKey,
                  portKey: leaf.portKey,
                },
              }))
              .sort((a, b) => {
                const byDate = sortByDateKeyDesc(a.selection.dateKey ?? '', b.selection.dateKey ?? '');
                if (byDate !== 0) return byDate;
                return sortLabel(a.label, b.label);
              });

            return {
              key: `${vKey}:${yKey}:${mKey}`,
              label: monthLabel(mKey),
              count: leafNodes.reduce((s, n) => s + n.count, 0),
              muted: mKey === NO_MONTH,
              icon: 'month' as const,
              iconClassName: 'text-sky-600 dark:text-sky-400',
              selection: { vesselKey: vKey, yearKey: yKey, monthKey: mKey },
              expandKey: `month:${vKey}:${yKey}:${mKey}`,
              children: leafNodes,
            };
          })
          .sort((a, b) => {
            const aKey = a.selection.monthKey ?? '';
            const bKey = b.selection.monthKey ?? '';
            if (aKey === NO_MONTH) return 1;
            if (bKey === NO_MONTH) return -1;
            return bKey.localeCompare(aKey);
          });

        return {
          key: `${vKey}:${yKey}`,
          label: yearLabel(yKey),
          count: monthNodes.reduce((s, n) => s + n.count, 0),
          muted: yKey === NO_YEAR,
          icon: 'year' as const,
          iconClassName: 'text-indigo-500 dark:text-indigo-400',
          selection: { vesselKey: vKey, yearKey: yKey },
          expandKey: `year:${vKey}:${yKey}`,
          children: monthNodes,
        };
      })
      .sort((a, b) => {
        const aKey = a.selection.yearKey ?? '';
        const bKey = b.selection.yearKey ?? '';
        if (aKey === NO_YEAR) return 1;
        if (bKey === NO_YEAR) return -1;
        return bKey.localeCompare(aKey);
      });

    return vesselNode(
      vKey,
      v.label,
      [orphanFolderNode(vKey, v.orphanReqIds), ...yearNodes],
      v.orphanReqIds.length,
    );
  });
}

export function buildPortCallTree(
  cases: PortCallCaseLike[],
  orphanRequisitions: PortCallRequisitionLike[] = [],
  groupBy: PortCallGroupBy = DEFAULT_PORT_CALL_GROUP_BY,
  formatDateFn: (iso?: string | null) => string = (iso) => iso ?? '',
): PortCallTreeNode[] {
  switch (groupBy) {
    case 'vessel-port-eta':
      return buildVesselPortEta(cases, orphanRequisitions, formatDateFn);
    case 'vessel-eta-port':
      return buildVesselEtaPort(cases, orphanRequisitions, formatDateFn);
    case 'port-vessel-eta':
      return buildPortVesselEta(cases, orphanRequisitions, formatDateFn);
    case 'eta-vessel-port':
      return buildEtaVesselPort(cases, orphanRequisitions, formatDateFn);
    case 'vessel-year-month-eta-port':
      return buildVesselYearMonthDatePort(cases, orphanRequisitions, formatDateFn);
    case 'vessel-date-port':
    default:
      return buildVesselDatePort(cases, orphanRequisitions, formatDateFn);
  }
}

export function selectionMatches(a: PortCallSelection, b: PortCallSelection): boolean {
  return (
    (a.vesselKey ?? null) === (b.vesselKey ?? null)
    && (a.folder ?? null) === (b.folder ?? null)
    && (a.yearKey ?? null) === (b.yearKey ?? null)
    && (a.monthKey ?? null) === (b.monthKey ?? null)
    && (a.portKey ?? null) === (b.portKey ?? null)
    && (a.dateKey ?? null) === (b.dateKey ?? null)
  );
}

export function filterCasesForSelection(
  cases: PortCallCaseLike[],
  selection: PortCallSelection | null,
): PortCallCaseLike[] {
  if (!selection || selection.folder === ORPHAN_REQS_FOLDER) return [];
  return cases.filter((c) => {
    if (selection.vesselKey != null && vesselKeyOf(c) !== selection.vesselKey) return false;
    if (selection.yearKey != null && etaYearKey(c.eta) !== selection.yearKey) return false;
    if (selection.monthKey != null && etaMonthKey(c.eta) !== selection.monthKey) return false;
    if (selection.dateKey != null && etaDateKey(c.eta) !== selection.dateKey) return false;
    if (selection.portKey != null && portKeyOf(c) !== selection.portKey) return false;
    return true;
  });
}

export function filterOrphanRequisitionsForSelection(
  requisitions: PortCallRequisitionLike[],
  selection: PortCallSelection | null,
): PortCallRequisitionLike[] {
  if (!selection || selection.folder !== ORPHAN_REQS_FOLDER) return [];
  if (selection.vesselKey == null) {
    return requisitions.filter((r) => r.caseId == null || r.caseId <= 0);
  }
  return requisitions.filter((r) => {
    if (r.caseId != null && r.caseId > 0) return false;
    return reqVesselKey(r) === selection.vesselKey;
  });
}

function findNodeBySelection(
  nodes: PortCallTreeNode[],
  selection: PortCallSelection,
): PortCallTreeNode | null {
  for (const node of nodes) {
    if (selectionMatches(node.selection, selection)) return node;
    if (node.children?.length) {
      const found = findNodeBySelection(node.children, selection);
      if (found) return found;
    }
  }
  return null;
}

export function selectionTitle(
  tree: PortCallTreeNode[],
  selection: PortCallSelection | null,
): string {
  if (!selection) return 'Select a node in the tree';
  if (selection.folder === ORPHAN_REQS_FOLDER) {
    const node = findNodeBySelection(tree, selection);
    if (node) {
      const parts: string[] = [];
      const walk = (nodes: PortCallTreeNode[], path: string[]): boolean => {
        for (const n of nodes) {
          const next = [...path, n.label];
          if (n === node) {
            parts.push(...next);
            return true;
          }
          if (n.children?.length && walk(n.children, next)) return true;
        }
        return false;
      };
      walk(tree, []);
      return parts.length > 0 ? parts.join(' · ') : 'Pending Requisitions';
    }
    return 'Pending Requisitions';
  }
  const node = findNodeBySelection(tree, selection);
  if (!node) return 'Select a node in the tree';
  const parts: string[] = [];
  const walk = (nodes: PortCallTreeNode[], path: string[]): boolean => {
    for (const n of nodes) {
      const next = [...path, n.label];
      if (n === node) {
        parts.push(...next);
        return true;
      }
      if (n.children?.length && walk(n.children, next)) return true;
    }
    return false;
  };
  walk(tree, []);
  return parts.length > 0 ? parts.join(' · ') : node.label;
}

export function expandedKeysForSelection(selection: PortCallSelection | null): Set<string> {
  const next = new Set<string>();
  if (!selection) return next;
  if (selection.folder === ORPHAN_REQS_FOLDER && !selection.vesselKey) {
    next.add('pending-root');
  }
  if (selection.portKey && !selection.vesselKey) {
    next.add(`port:${selection.portKey}`);
  }
  if (selection.dateKey && !selection.vesselKey) {
    next.add(`eta:${selection.dateKey}`);
  }
  if (selection.portKey && selection.vesselKey) {
    next.add(`port:${selection.portKey}`);
    next.add(`vessel:${selection.portKey}:${selection.vesselKey}`);
  }
  if (selection.dateKey && selection.vesselKey && !selection.portKey) {
    next.add(`eta:${selection.dateKey}`);
    next.add(`vessel:${selection.dateKey}:${selection.vesselKey}`);
  }
  if (selection.vesselKey && !selection.portKey && !selection.dateKey) {
    next.add(`vessel:${selection.vesselKey}`);
  }
  if (selection.vesselKey && selection.portKey && !selection.dateKey) {
    // vessel-port-eta intermediate
    next.add(`vessel:${selection.vesselKey}`);
    next.add(`port:${selection.vesselKey}:${selection.portKey}`);
  }
  if (selection.vesselKey && selection.dateKey && selection.portKey && !selection.yearKey) {
    next.add(`vessel:${selection.vesselKey}`);
    // vessel-eta-port path
    next.add(`eta:${selection.vesselKey}:${selection.dateKey}`);
    // vessel-port-eta path also needs port
    next.add(`port:${selection.vesselKey}:${selection.portKey}`);
  }
  if (selection.vesselKey && selection.dateKey && !selection.portKey) {
    next.add(`vessel:${selection.vesselKey}`);
    next.add(`eta:${selection.vesselKey}:${selection.dateKey}`);
  }
  if (selection.vesselKey && selection.yearKey) {
    next.add(`vessel:${selection.vesselKey}`);
    next.add(`year:${selection.vesselKey}:${selection.yearKey}`);
  }
  if (selection.vesselKey && selection.yearKey && selection.monthKey) {
    next.add(`month:${selection.vesselKey}:${selection.yearKey}:${selection.monthKey}`);
  }
  return next;
}

export function coerceSelectionToTree(
  tree: PortCallTreeNode[],
  selection: PortCallSelection | null,
): PortCallSelection | null {
  if (tree.length === 0) return null;
  if (!selection) return tree[0].selection;
  if (findNodeBySelection(tree, selection)) return selection;

  const candidates: PortCallSelection[] = [
    selection,
    {
      vesselKey: selection.vesselKey,
      yearKey: selection.yearKey,
      monthKey: selection.monthKey,
      dateKey: selection.dateKey,
    },
    {
      vesselKey: selection.vesselKey,
      yearKey: selection.yearKey,
      monthKey: selection.monthKey,
    },
    { vesselKey: selection.vesselKey, yearKey: selection.yearKey },
    { vesselKey: selection.vesselKey, portKey: selection.portKey },
    { vesselKey: selection.vesselKey, dateKey: selection.dateKey },
    { portKey: selection.portKey, vesselKey: selection.vesselKey },
    { dateKey: selection.dateKey, vesselKey: selection.vesselKey },
    { portKey: selection.portKey },
    { dateKey: selection.dateKey },
    { vesselKey: selection.vesselKey },
  ];
  for (const cand of candidates) {
    if (findNodeBySelection(tree, cand)) return cand;
  }
  return tree[0].selection;
}

export function filterTreeByQuery(tree: PortCallTreeNode[], query: string): PortCallTreeNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return tree;
  const filterNode = (node: PortCallTreeNode): PortCallTreeNode | null => {
    const selfMatch = node.label.toLowerCase().includes(q);
    if (!node.children?.length) return selfMatch ? node : null;
    const kids = node.children.map(filterNode).filter(Boolean) as PortCallTreeNode[];
    if (selfMatch) return { ...node, children: node.children };
    if (kids.length > 0) return { ...node, children: kids };
    return null;
  };
  return tree.map(filterNode).filter(Boolean) as PortCallTreeNode[];
}

export function collectExpandKeys(nodes: PortCallTreeNode[]): string[] {
  const keys: string[] = [];
  const walk = (list: PortCallTreeNode[]) => {
    for (const n of list) {
      if (n.expandKey) keys.push(n.expandKey);
      if (n.children?.length) walk(n.children);
    }
  };
  walk(nodes);
  return keys;
}
